import { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../components/ui/card";
import Button from "../../../components/ui/button";
import { ChevronDown, ChevronRight, Trash2, Plus  } from "lucide-react";
import { Input } from "../../../components/ui/input";
import EmployeeSelector from "./EmployeeSelector";
function formatMinutesToHHMM(mins) {
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function parseHHMMToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}
/* --------------------------------------------------------------
   Collapsible <RuleCard /> – replaces the old non‑collapsible one
----------------------------------------------------------------*/
export function RuleCard({ rule, employees, onUpdate, onDelete, error, disabledEmployeeIds = [] }) {
  const toggleExpanded = () => onUpdate({ ...rule, expanded: !rule.expanded });

  const setField = (path, val) => {
    const next = structuredClone(rule);
    const parts = path.split(".");
    let ptr = next.config;
    parts.slice(0, -1).forEach((p) => (ptr = ptr[p]));
    ptr[parts.at(-1)] = val;
    onUpdate(next);
  };

  return (
    <Card className="border shadow-sm overflow-visible">
      {/* ───── Header ───── */}
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
            {rule.expanded ? (
                <ChevronDown
                    size={18}
                    className="cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();     // keep the delete button from closing, etc.
                        toggleExpanded();
                    }}
                />
            ) : (
                <ChevronRight
                    size={18}
                    className="cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded();
                    }}
                />
            )}
     <CardTitle className="text-base">Rule</CardTitle>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(rule.id);
          }}
        >
          <Trash2 size={16} />
        </Button>
      </CardHeader>

      {/* ───── Body ───── */}
      {rule.expanded && (
        <CardContent className="space-y-6 pt-0 pb-4">
          {/* Basic fields – you can extend this exactly as before */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <label className="form-label">
              Max hours / week
              <Input
                type="number"
                className="form-input"
                value={rule.config.employeeLimits.maxHoursPerWeek}
                onChange={(e) =>
                  setField("employeeLimits.maxHoursPerWeek", +e.target.value)
                }
              />
            </label>
            <label className="form-label">
              Min hours / week
              <Input
                type="number"
                className="form-input"
                value={rule.config.employeeLimits.minHoursPerWeek || 0}
                onChange={(e) =>
                  setField("employeeLimits.minHoursPerWeek", +e.target.value)
                }
              />
            </label>
            <label className="form-label">
              Min days‑off / week
              <Input
                type="number"
                className="form-input"
                value={rule.config.employeeLimits.minDaysOffPerWeek || 0}
                onChange={(e) =>
                  setField("employeeLimits.minDaysOffPerWeek", +e.target.value)
                }
              />
            </label>
            <label className="form-label">
                Max paid days off / year
                <Input
                    type="number"
                    className="form-input"
                    value={rule.config.employeeLimits.maxDaysOffPerYear || 0}
                    onChange={(e) =>
                    setField("employeeLimits.maxDaysOffPerYear", +e.target.value)
                    }
                />
            </label>
            {/* ✅ New Max Sick Days Field */}
            <label className="form-label">
              Max paid sick days / year
              <Input
                type="number"
                className="form-input"
                value={rule.config.employeeLimits.maxSickDaysPerYear || 0}
                onChange={(e) =>
                  setField("employeeLimits.maxSickDaysPerYear", +e.target.value)
                }
              />
            </label>
          </div>
    {/* Preferred Start Times */}
    <div>
    <label className="form-label block mb-1">
        Preferred shift start time
    </label>

    <div className="flex gap-2 items-center mb-2">
        <input
        type="time"
        value={rule._tmpNewStartTime || ""}
        onChange={(e) =>
            onUpdate({ ...rule, _tmpNewStartTime: e.target.value })
        }
        className="form-input w-[120px]"
        />
        <Button
        type="button"
        size="sm"
        onClick={() => {
            const timeStr = rule._tmpNewStartTime;
            if (!timeStr) return;

            const newMinutes = parseHHMMToMinutes(timeStr);
            const existing = rule.config.shiftRules.preferredShiftStartTimes || [];

            if (!existing.includes(newMinutes) && existing.length < 1) {
                const updatedRule = {
                    ...rule,
                    _tmpNewStartTime: "",
                    config: {
                        ...rule.config,
                        shiftRules: {
                            ...rule.config.shiftRules,
                            preferredShiftStartTimes: [...existing, newMinutes].sort((a, b) => a - b),
                        },
                    },
                };
                onUpdate(updatedRule);
            }
        }}

        disabled={
            !rule._tmpNewStartTime ||
            (rule.config.shiftRules.preferredShiftStartTimes?.length || 0) >= 5
        }
        >
        <Plus size={16} />
        Add
        </Button>
    </div>

    <div className="space-y-1">
        {(rule.config.shiftRules.preferredShiftStartTimes || []).map((mins, idx) => (
        <div key={idx} className="flex items-center justify-between">
            <span className="text-sm font-mono">{formatMinutesToHHMM(mins)}</span>
            <button
            type="button"
            onClick={() => {
                const next = rule.config.shiftRules.preferredShiftStartTimes.filter(
                (m) => m !== mins
                );
                setField("shiftRules.preferredShiftStartTimes", next);
            }}
            className="text-red-500 hover:text-red-700"
            >
            <Trash2 size={14} />
            </button>
        </div>
        ))}
        {rule.config.shiftRules.preferredShiftStartTimes?.length >= 5 && (
        <p className="text-xs text-gray-500">Limit: max 5 preferred times</p>
        )}
    </div>
    </div>
  
      {/* Enforce Consistent Shift Times */}
      {(rule.config.shiftRules.preferredShiftStartTimes?.length ?? 0) > 0 && (
          <label className="flex items-center gap-2 mt-2">
                <input
                    type="checkbox"
                    className="checkbox"
                    checked={rule.config.shiftRules.enforceConsistentShiftTimes || false}
                    onChange={(e) =>
                        setField("shiftRules.enforceConsistentShiftTimes", e.target.checked)
                    }
                />
                Enforce consistent shift time
          </label>
      )}

          {/* Assignment */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              className="checkbox"
              checked={rule.applyToAll}
              onChange={(e) =>
                onUpdate({
                  ...rule,
                  applyToAll: e.target.checked,
                  employees: [],
                })
              }
            />
            Apply to all employees
          </label>
          {!rule.applyToAll && (
            <EmployeeSelector
              employees={employees}
              selected={rule.employees}
              onChange={(list) => onUpdate({ ...rule, employees: list })}
              disabledIds={disabledEmployeeIds}
            />
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </CardContent>
      )}
    </Card>
  );
}